import { supabase } from "@/integrations/supabase/client";
import {
  AI_SCAN_PHOTO_LOAD_ALL,
  AI_SCAN_UNAVAILABLE,
  aiScanPhotoLoadPartial,
  sanitizeAiUserMessage,
  userMessageForScanErrorCode,
} from "@/lib/aiUserFacingErrors";
import { AnalyzePhotoError, analyzeByImageUrl } from "@/lib/analyzeInventoryPhoto";
import { normalizeRoomName } from "@/lib/roomUtils";

export type ListingItemDestination =
  | "supply_closet"
  | "guest_amenities"
  | "unit_check"
  | "cleaning_task"
  | "maintenance"
  | "skip";

export type ListingScanPhoto = { url: string; roomName: string };

export type ListingScanDetectedItem = {
  id: string;
  name: string;
  roomName: string;
  sourcePhotoUrl: string;
  confidence: number;
  evidence?: string;
  /** Cleaning instruction — only present when destination is "cleaning_task". */
  suggestedTask?: string;
  destination: ListingItemDestination;
  included: boolean;
};

export type PendingWizardProofStandard = {
  title: string;
  room: string;
  referencePhotoUrl: string;
};

/** One photo per room (first assigned photo for that room). */
export function pickOnePhotoPerRoom(photos: ListingScanPhoto[]): ListingScanPhoto[] {
  const seen = new Set<string>();
  const out: ListingScanPhoto[] = [];
  for (const p of photos) {
    const key = normalizeRoomName(p.roomName);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(p);
  }
  return out;
}

export function defaultDestinationFromCategory(category?: string): ListingItemDestination {
  const c = (category ?? "").toLowerCase();
  if (c === "consumable" || c === "cleaning") return "supply_closet";
  if (c === "appliance" || c === "device") return "unit_check";
  return "unit_check";
}

function normItemKey(name: string, roomName: string, destination: ListingItemDestination): string {
  return `${normalizeRoomName(roomName)}::${destination}::${name.trim().toLowerCase()}`;
}

/** Merge detections across rooms; keep highest confidence per name+room+destination. */
export function dedupeListingItems(items: ListingScanDetectedItem[]): ListingScanDetectedItem[] {
  const byKey = new Map<string, ListingScanDetectedItem>();
  for (const item of items) {
    const key = normItemKey(item.name, item.roomName, item.destination);
    const prev = byKey.get(key);
    if (!prev || item.confidence > prev.confidence) byKey.set(key, item);
  }
  return [...byKey.values()].sort((a, b) => {
    const room = a.roomName.localeCompare(b.roomName);
    if (room !== 0) return room;
    return a.name.localeCompare(b.name);
  });
}

export type ListingRoomScanFailure = {
  roomName: string;
  message: string;
  error_code?: string;
};

export type ListingScanResult = {
  items: ListingScanDetectedItem[];
  failures: ListingRoomScanFailure[];
};

export class ListingScanAllRoomsFailedError extends Error {
  readonly failures: ListingRoomScanFailure[];

  constructor(message: string, failures: ListingRoomScanFailure[]) {
    super(message);
    this.name = "ListingScanAllRoomsFailedError";
    this.failures = failures;
  }
}

/** Owner-facing hint when one or more room scans failed (no internal/vendor details). */
export function summarizeListingScanFailures(
  failures: ListingRoomScanFailure[],
  roomCount: number
): string {
  if (failures.length === 0) return AI_SCAN_UNAVAILABLE;

  const imageFailures = failures.filter((f) => f.error_code === "IMAGE_FETCH_FAILED");
  if (imageFailures.length >= roomCount) return AI_SCAN_PHOTO_LOAD_ALL;
  if (imageFailures.length > 0) return aiScanPhotoLoadPartial(imageFailures.length);

  const rateLimited = failures.every((f) => f.error_code === "CLAUDE_RATE_LIMIT");
  if (rateLimited) return userMessageForScanErrorCode("CLAUDE_RATE_LIMIT") ?? AI_SCAN_UNAVAILABLE;

  const infraCodes = new Set([
    "CLAUDE_INSUFFICIENT_CREDITS",
    "CLAUDE_ERROR",
    "CLAUDE_AUTH",
    "SERVER_MISCONFIGURED",
    "OPENAI_ERROR",
    "EMPTY_MODEL_RESPONSE",
    "INVALID_MODEL_JSON",
  ]);
  if (failures.every((f) => (f.error_code && infraCodes.has(f.error_code)) || !f.error_code)) {
    return AI_SCAN_UNAVAILABLE;
  }

  const first = failures[0];
  return (
    sanitizeAiUserMessage(first?.message ?? "", first?.error_code) || AI_SCAN_UNAVAILABLE
  );
}

export async function scanListingPhotos(
  propertyId: string,
  photos: ListingScanPhoto[],
  onProgress?: (done: number, total: number, roomName: string) => void
): Promise<ListingScanResult> {
  const perRoom = pickOnePhotoPerRoom(photos);
  const all: ListingScanDetectedItem[] = [];
  const failures: ListingRoomScanFailure[] = [];
  let done = 0;

  for (const photo of perRoom) {
    onProgress?.(done, perRoom.length, photo.roomName);
    try {
      const result = await analyzeByImageUrl(propertyId, photo.url, "", {
        roomHint: photo.roomName,
        mode: "listing",
      });
      const VALID_DESTS: ListingItemDestination[] = [
        "supply_closet", "guest_amenities", "unit_check", "cleaning_task", "maintenance", "skip",
      ];
      for (const row of result.items) {
        const destRaw = (row as { suggested_destination?: string }).suggested_destination;
        const destination: ListingItemDestination = VALID_DESTS.includes(destRaw as ListingItemDestination)
          ? (destRaw as ListingItemDestination)
          : defaultDestinationFromCategory(row.category);
        if (destination === "skip") continue;
        const conf = typeof row.confidence === "number" ? row.confidence : 0.7;
        const suggestedTask = (row as { suggested_task?: string }).suggested_task?.trim() || undefined;
        all.push({
          id: crypto.randomUUID(),
          name: row.name.trim(),
          roomName: photo.roomName,
          sourcePhotoUrl: photo.url,
          confidence: conf,
          evidence: row.evidence,
          suggestedTask: destination === "cleaning_task" ? suggestedTask : undefined,
          destination,
          included: conf >= 0.75,
        });
      }
    } catch (e) {
      const error_code = e instanceof AnalyzePhotoError ? e.error_code : undefined;
      const rawMessage =
        e instanceof AnalyzePhotoError
          ? e.message
          : e instanceof Error
            ? e.message
            : "Scan failed";
      const message = sanitizeAiUserMessage(rawMessage, error_code);
      failures.push({ roomName: photo.roomName, message, error_code });
      console.error("[listingPhotoScan] room failed", photo.roomName, e);
    }
    done += 1;
    onProgress?.(done, perRoom.length, photo.roomName);
  }

  const items = dedupeListingItems(all);
  if (items.length === 0 && failures.length > 0 && failures.length >= perRoom.length) {
    throw new ListingScanAllRoomsFailedError(
      summarizeListingScanFailures(failures, perRoom.length),
      failures
    );
  }

  return { items, failures };
}

/** Ensure property_rooms exist for each name (idempotent). */
export async function ensurePropertyRooms(propertyId: string, roomNames: string[]): Promise<void> {
  const { data: existing } = await supabase
    .from("property_rooms")
    .select("name_normalized")
    .eq("property_id", propertyId)
    .is("archived_at", null);

  const existingNorm = new Set((existing ?? []).map((r) => r.name_normalized));

  for (const name of roomNames) {
    const trimmed = name.trim();
    if (!trimmed) continue;
    const norm = normalizeRoomName(trimmed);
    if (existingNorm.has(norm)) continue;
    const { error } = await supabase.from("property_rooms").insert({
      property_id: propertyId,
      name: trimmed,
      name_normalized: norm,
    });
    if (error && !error.message.includes("duplicate") && !error.message.includes("already exists")) {
      throw error;
    }
    existingNorm.add(norm);
  }
}

/** Resolve inventory_rooms.id for a display room name (creates property + inventory room if needed). */
export async function getOrCreateInventoryRoomId(
  propertyId: string,
  roomName: string
): Promise<string | null> {
  const trimmed = roomName.trim();
  if (!trimmed) return null;
  const norm = normalizeRoomName(trimmed);

  const { data: propRoom } = await supabase
    .from("property_rooms")
    .select("id, name")
    .eq("property_id", propertyId)
    .eq("name_normalized", norm)
    .maybeSingle();

  let propertyRoomId = propRoom?.id;
  let canonicalName = propRoom?.name ?? trimmed;

  if (!propertyRoomId) {
    const { data: created, error } = await supabase
      .from("property_rooms")
      .insert({ property_id: propertyId, name: trimmed, name_normalized: norm })
      .select("id, name")
      .single();
    if (error) throw error;
    propertyRoomId = created.id;
    canonicalName = created.name;
  }

  const { data: invRoom } = await supabase
    .from("inventory_rooms")
    .select("id")
    .eq("property_id", propertyId)
    .eq("property_room_id", propertyRoomId)
    .maybeSingle();

  if (invRoom?.id) return invRoom.id;

  const { data: maxOrder } = await supabase
    .from("inventory_rooms")
    .select("order_index")
    .eq("property_id", propertyId)
    .order("order_index", { ascending: false })
    .limit(1)
    .maybeSingle();

  const { data: newInv, error: invErr } = await supabase
    .from("inventory_rooms")
    .insert({
      property_id: propertyId,
      name: canonicalName,
      order_index: (maxOrder?.order_index ?? 0) + 1,
      property_room_id: propertyRoomId,
    })
    .select("id")
    .single();

  if (invErr) throw invErr;
  return newInv.id;
}

export const DESTINATION_LABELS: Record<ListingItemDestination, string> = {
  cleaning_task: "Cleaning checklist task",
  unit_check: "Unit Check — photo verify",
  supply_closet: "Inventory — restock",
  guest_amenities: "Guest amenities",
  maintenance: "Appliance log",
  skip: "Skip",
};

export type PendingWizardCleaningTask = {
  taskText: string;
  room: string;
};
